/**
 * Configurateur Pack Gifting (écran 04 BFCM).
 *
 * Une seule étape : la cliente compose son pack parmi les variantes (coloris)
 * d'un seul produit. Une ligne panier est générée par écharpe (quantité 1) avec
 * les propriétés Pack / _pack_id / _pack_slot, utilisées par le panier (écran 05)
 * pour le détail du pack et la fonction « Modifier mon pack ».
 *
 * « Modifier mon pack » : rouvrir /pages/pack-gifting?selection=<variantId>:<qty>,...
 * pour pré-remplir le configurateur (le paramètre URL prime sur le sessionStorage).
 *
 * Étape bandeau : quand le pack est complet, un encart « Choisissez votre bandeau
 * offert » apparaît dans la synthèse ; le coloris choisi part en sessionStorage
 * (packGiftChoice_<productId>) — le bandeau n'est PAS dans le bundle, c'est
 * assets/cart-gift.js qui l'ajoute au panier (palier jauge + remise Smart Function).
 *
 * Lightbox galerie : clic sur la pastille d'une ligne → photos du produit du coloris
 * en grand (fetch /products/<handle>.js à la 1re ouverture, puis cache). Slider
 * scroll-snap (swipe natif mobile, flèches desktop), navigation coloris préc/suiv
 * et CTA « Ajouter au pack » synchronisé avec la ligne.
 *
 * Dépendances du thème : formatPrice() (snippets/deployer-tools.liquid, chargé
 * dans layout/theme.liquid) et l'élément <product-form> (assets/product-form.js).
 */
if (!customElements.get('pack-gifting-configurator')) {
  customElements.define(
    'pack-gifting-configurator',

    class PackGiftingConfigurator extends HTMLElement {
      constructor() {
        super();
        this.paidSlots = parseInt(this.dataset.paidSlots, 10);
        this.freeSlots = parseInt(this.dataset.freeSlots, 10);
        this.totalSlots = parseInt(this.dataset.totalSlots, 10);
        this.unitPrice = parseInt(this.dataset.unitPrice, 10);
        this.packName = this.dataset.packName;
        // Préfixe de _pack_id/_bundleUniqueId : pg- gifting (5 slots), pt- trio (3 slots,
        // ?view=pack-trio) — permet aux règles Smart Functions de distinguer les 2 offres.
        this.packPrefix = this.totalSlots === 3 ? 'pt-' : 'pg-';
        // Le produit offert (bandeau) n'est PLUS injecté dans le bundle : il est ajouté
        // au panier par assets/cart-gift.js (palier 150€ de la jauge) et remisé à 100 %
        // par la Smart Function — pas de doublon possible entre pack et palier panier.
        // Étape « Choisissez votre bandeau offert » : affichée quand le pack est complet ;
        // le choix part en sessionStorage (packGiftChoice_<productId>) — lu par cart-gift.js.
        this.giftProductId = parseInt(this.dataset.giftProductId, 10) || null;
        this.giftPicker = this.querySelector('[data-gift-picker]');
        this.giftChoice = this.giftProductId
          ? sessionStorage.getItem(`packGiftChoice_${this.giftProductId}`)
          : null;
        this.ctaGiftText = this.querySelector('.pack-gifting--cta-gift-text');
        // Clé par config (totalSlots) : la page Gifting (3+2) et la page Trio (2+1, ?view=pack-trio)
        // partagent le même produit — sans ça une sélection 5 unités se restaurerait sur 3 emplacements.
        this.storageKey = `packGiftingSelection_${this.dataset.productId}_${this.totalSlots}`;

        // Map<variantId, { qty, price, swatchBg }> — l'ordre d'insertion = ordre de remplissage des emplacements
        this.selection = new Map();
        this.packId = null;

        this.slots = this.querySelectorAll('.pack-gifting--slot');
        this.cta = this.querySelector('.pack-gifting--cta');
        this.ctaCount = this.querySelector('.pack-gifting--cta-count');
        this.form = this.querySelector('form');
        this.formInputs = this.querySelector('.pack-gifting--form-inputs');
        this.summary = this.querySelector('.pack-gifting--summary');
        this.summaryItems = this.querySelector('.pack-gifting--summary-items');

        // « Modifier mon pack » (écran 05) : l'ancien pack est retiré du panier
        // avant le nouvel ajout, sinon la modification dupliquerait les lignes.
        this.oldPackId = new URLSearchParams(window.location.search).get('pack');
        // Pack mergé (cart transform) : le parent bundle est retiré directement par sa clé de ligne
        this.oldBundleKey = new URLSearchParams(window.location.search).get('bundle_key');

        // Lightbox galerie coloris : construite à la première ouverture (clic pastille).
        // Keydown en phase CAPTURE + stopPropagation : sinon Escape fermerait aussi
        // la modale pack (pack-gifting-modal écoute en bulle sur document).
        this.lightbox = null;
        this.lightboxLine = null;
        this.lightboxIndex = 0;
        // Réglage « Photo affichée à l'ouverture » : 1 = photo du coloris, 2+ = N-ième photo
        this.lightboxStartImage = parseInt(this.dataset.lightboxStartImage, 10) || 1;
        this.productCache = {};
        this.lightboxKeydown = (event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            this.closeLightbox();
          } else if (event.key === 'ArrowLeft') {
            this.goToImage(this.lightboxIndex - 1);
          } else if (event.key === 'ArrowRight') {
            this.goToImage(this.lightboxIndex + 1);
          }
        };
      }

      connectedCallback() {
        this.addEventListener('click', (event) => {
          // Étape bandeau : choix du coloris offert (prime sur tout le reste)
          const giftOption = event.target.closest('.pack-gifting--gift-option');
          if (giftOption) {
            if (!giftOption.disabled) this.selectGiftOption(giftOption);
            return;
          }

          // Clic sur la pastille : galerie du coloris en lightbox (prime sur tout)
          const lightboxTrigger = event.target.closest('[data-open-lightbox]');
          if (lightboxTrigger) {
            const triggerLine = lightboxTrigger.closest('.pack-gifting--line');
            if (triggerLine) this.openLightbox(triggerLine);
            return;
          }

          // Mobile/tablette : repli/dépli du détail de la synthèse (replié par défaut sous 990px)
          const detailToggle = event.target.closest('.pack-gifting--detail-toggle');
          if (detailToggle) {
            const open = this.summary.classList.toggle('pack-gifting--summary--open');
            detailToggle.setAttribute('aria-expanded', String(open));
            return;
          }

          // Bouton « Vider mon pack » : vide toute la sélection
          const clearPack = event.target.closest('[data-clear-pack]');
          if (clearPack) {
            this.selection.clear();
            this.packId = null;
            this.persistSelection();
            this.render();
            return;
          }

          // Bouton « × » du récap de synthèse : retire toutes les unités du coloris
          const summaryRemove = event.target.closest('[data-summary-remove]');
          if (summaryRemove) {
            this.selection.delete(summaryRemove.dataset.summaryRemove);
            if (this.selection.size === 0) this.packId = null;
            this.persistSelection();
            this.render();
            return;
          }

          const line = event.target.closest('.pack-gifting--line');
          if (!line || line.classList.contains('pack-gifting--line--oos')) return;

          if (event.target.closest('.pack-gifting--add')) {
            this.addUnit(line);
          } else if (event.target.closest('[data-action="increase"]')) {
            this.addUnit(line);
          } else if (event.target.closest('[data-action="decrease"]')) {
            this.removeUnit(line);
          }
        });

        // Capture : prime sur l'intercepteur d'ajout au panier du drawer
        this.cta.addEventListener('click', (event) => this.handleOldPackRemoval(event), true);

        this.restoreSelection();
        this.restoreGiftChoice();
        this.render();
      }

      /* --- Étape « Choisissez votre bandeau offert » --- */

      // Restaure le coloris choisi à la session précédente (variante retirée /
      // épuisée entre-temps → choix effacé, la cliente rechoisit)
      restoreGiftChoice() {
        if (!this.giftPicker || !this.giftChoice) return;
        const option = this.giftPicker.querySelector(
          `.pack-gifting--gift-option[data-gift-variant-id="${this.giftChoice}"]`
        );
        if (option && !option.disabled) {
          option.classList.add('pack-gifting--gift-option--selected');
        } else {
          this.giftChoice = null;
          sessionStorage.removeItem(`packGiftChoice_${this.giftProductId}`);
        }
      }

      selectGiftOption(option) {
        this.giftPicker
          .querySelectorAll('.pack-gifting--gift-option--selected')
          .forEach((el) => el.classList.remove('pack-gifting--gift-option--selected'));
        option.classList.add('pack-gifting--gift-option--selected');
        this.giftChoice = option.dataset.giftVariantId;
        sessionStorage.setItem(`packGiftChoice_${this.giftProductId}`, this.giftChoice);
        // Un choix explicite ré-arme le cadeau (efface un éventuel retrait manuel antérieur)
        sessionStorage.removeItem(`cartGiftDeclined_${this.giftProductId}`);
        this.render();
      }

      // Le bandeau ne se propose que sur un pack complet (caché sinon, choix conservé).
      // Tous les coloris épuisés → étape ignorée pour ne pas bloquer l'ajout au panier.
      renderGiftPicker() {
        if (!this.giftPicker) return;
        const hasAvailableOption = Boolean(
          this.giftPicker.querySelector('.pack-gifting--gift-option:not(:disabled)')
        );
        this.giftPicker.hidden = this.unitCount < this.totalSlots || !hasAvailableOption;
      }

      async handleOldPackRemoval(event) {
        if ((!this.oldPackId && !this.oldBundleKey) || this.cta.disabled) return;

        // Retire l'ancien pack avant de laisser le drawer ajouter la nouvelle sélection
        event.preventDefault();
        event.stopImmediatePropagation();

        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
          const updates = {};
          cart.items.forEach((item) => {
            if (item.properties && item.properties._pack_id === this.oldPackId) updates[item.key] = 0;
            if (this.oldBundleKey && item.key === this.oldBundleKey) updates[item.key] = 0;
          });
          if (Object.keys(updates).length > 0) {
            await fetch(`${routes.cart_update_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            });
          }
        } catch (e) {
          console.error('Suppression de l\'ancien pack impossible', e);
        }

        this.oldPackId = null;
        this.oldBundleKey = null;
        // Relance le clic : le drawer intercepte et ajoute la nouvelle sélection
        this.cta.click();
      }

      get unitCount() {
        let count = 0;
        this.selection.forEach((entry) => (count += entry.qty));
        return count;
      }

      addUnit(line) {
        // Pack complet : les boutons ne font plus rien, pas de message d'erreur
        if (this.unitCount >= this.totalSlots) return;

        const variantId = line.dataset.variantId;
        if (!this.packId) this.packId = `${this.packPrefix}${Date.now().toString(36)}`;

        const entry = this.selection.get(variantId) || {
          qty: 0,
          price: parseInt(line.dataset.price, 10),
          swatchBg: line.dataset.swatchBg,
        };
        entry.qty += 1;
        this.selection.set(variantId, entry);

        this.persistSelection();
        this.render();
      }

      removeUnit(line) {
        const variantId = line.dataset.variantId;
        const entry = this.selection.get(variantId);
        if (!entry) return;

        entry.qty -= 1;
        if (entry.qty <= 0) {
          // Quantité à zéro : l'emplacement est rendu, la ligne repasse à « Ajouter + »
          this.selection.delete(variantId);
        }

        if (this.selection.size === 0) this.packId = null;

        this.persistSelection();
        this.render();
      }

      render() {
        this.renderLines();
        this.renderSlots();
        this.renderGiftPicker();
        this.renderCta();
        this.renderSummary();
        this.updateFormInputs();
        this.syncLightboxAction();
      }

      formatMoney(cents) {
        if (typeof formatPrice === 'function') return formatPrice(cents);
        return (cents / 100).toFixed(2).replace('.', ',') + ' €';
      }

      // Synthèse : état vide vs liste des articles + wording adapté au contenu (maquette 04).
      // Les unités remplissent d'abord les emplacements payants, puis les offerts :
      // compteurs et montants suivent la sélection (1 écharpe — 60€, puis 2 — 120€…
      // et la ligne « offerte » n'apparaît qu'à partir de la 1re unité offerte).
      renderSummary() {
        if (!this.summary) return;
        const count = this.unitCount;
        this.summary.classList.toggle('pack-gifting--summary--empty', count === 0);

        const paidCount = Math.min(count, this.paidSlots);
        const freeCount = Math.max(0, count - this.paidSlots);

        const paidLine = this.summary.querySelector('.pack-gifting--summary-line--paid');
        if (paidLine) {
          paidLine.classList.toggle('hidden', paidCount === 0);
          const countEl = paidLine.querySelector('.pack-gifting--summary-paid-count');
          const amountEl = paidLine.querySelector('.pack-gifting--summary-paid-amount');
          if (countEl) countEl.textContent = paidCount;
          if (amountEl) amountEl.textContent = this.formatMoney(paidCount * this.unitPrice);
        }

        const freeLine = this.summary.querySelector('.pack-gifting--summary-line--free');
        if (freeLine) {
          freeLine.classList.toggle('hidden', freeCount === 0);
          const countEl = freeLine.querySelector('.pack-gifting--summary-free-count');
          const amountEl = freeLine.querySelector('.pack-gifting--summary-free-amount');
          if (countEl) countEl.textContent = freeCount;
          if (amountEl) amountEl.textContent = `− ${this.formatMoney(freeCount * this.unitPrice)}`;
        }

        if (!this.summaryItems) return;

        this.summaryItems.innerHTML = '';
        this.selection.forEach((entry, variantId) => {
          const line = this.querySelector(`.pack-gifting--line[data-variant-id="${variantId}"]`);
          const name = line?.querySelector('.pack-gifting--line-name')?.textContent.trim() || '';

          const item = document.createElement('li');
          item.className = 'pack-gifting--summary-item';

          const swatch = document.createElement('span');
          swatch.className = 'pack-gifting--summary-item-swatch';
          // Pastille couleur « pure » en priorité dans la récap (jamais la photo si une couleur existe)
          const swatchColor = line?.dataset.swatchColor || entry.swatchBg;
          if (swatchColor) swatch.style.background = swatchColor;

          const label = document.createElement('span');
          label.className = 'pack-gifting--summary-item-name';
          label.textContent = name;

          const qty = document.createElement('span');
          qty.className = 'pack-gifting--summary-item-qty';
          qty.textContent = `× ${entry.qty}`;

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'pack-gifting--summary-item-remove';
          remove.dataset.summaryRemove = variantId;
          remove.textContent = '×';
          remove.setAttribute('aria-label', `Retirer ${name}`);

          item.append(swatch, label, qty, remove);
          this.summaryItems.appendChild(item);
        });
      }

      renderLines() {
        const isComplete = this.unitCount >= this.totalSlots;
        this.querySelectorAll('.pack-gifting--line').forEach((line) => {
          const entry = this.selection.get(line.dataset.variantId);
          const qty = entry ? entry.qty : 0;
          const addButton = line.querySelector('.pack-gifting--add');
          const stepper = line.querySelector('.pack-gifting--stepper');
          if (!addButton || !stepper) return;

          addButton.classList.toggle('hidden', qty > 0);
          addButton.classList.toggle('pack-gifting--add--inert', isComplete);
          stepper.classList.toggle('hidden', qty === 0);
          stepper.querySelector('.pack-gifting--stepper-qty').textContent = qty;
          stepper
            .querySelector('[data-action="increase"]')
            .classList.toggle('pack-gifting--stepper-btn--inert', isComplete);
        });
      }

      renderSlots() {
        // Aplatit la sélection en unités : l'ordre d'insertion remplit les emplacements de gauche à droite
        const units = [];
        this.selection.forEach((entry) => {
          for (let i = 0; i < entry.qty; i++) units.push(entry);
        });

        this.slots.forEach((slot, index) => {
          const unit = units[index];
          slot.classList.toggle('pack-gifting--slot--filled', Boolean(unit));
          if (unit && unit.swatchBg) {
            slot.style.setProperty('--slot-fill', unit.swatchBg);
          } else {
            slot.style.removeProperty('--slot-fill');
          }
        });
      }

      renderCta() {
        const count = this.unitCount;
        const isComplete = count >= this.totalSlots;
        // Pack complet + bandeau proposé mais pas encore choisi : le CTA bascule
        // sur l'étape bandeau (« CHOISISSEZ VOTRE BANDEAU OFFERT »), ajout bloqué
        const needsGiftChoice = isComplete && this.giftPicker && !this.giftPicker.hidden && !this.giftChoice;
        this.ctaCount.textContent = count;
        this.cta.disabled = !isComplete || needsGiftChoice;
        this.querySelector('.pack-gifting--cta-progress-text').classList.toggle('hidden', isComplete);
        this.querySelector('.pack-gifting--cta-complete-text').classList.toggle(
          'hidden',
          !isComplete || needsGiftChoice
        );
        if (this.ctaGiftText) this.ctaGiftText.classList.toggle('hidden', !needsGiftChoice);
      }

      updateFormInputs() {
        this.formInputs.querySelectorAll('input').forEach((input) => input.remove());

        // _pack_slot est unique par unité (paid-1, free-1…) : empêche Shopify de fusionner
        // les lignes identiques et permet au panier (écran 05) d'afficher le détail par écharpe.
        let index = 0;
        let paidIndex = 0;
        let freeIndex = 0;
        this.selection.forEach((entry, variantId) => {
          for (let i = 0; i < entry.qty; i++) {
            const slotValue = index < this.paidSlots ? `paid-${++paidIndex}` : `free-${++freeIndex}`;
            const inputs = [
              { name: `items[${index}][id]`, value: variantId },
              { name: `items[${index}][quantity]`, value: 1 },
              { name: `items[${index}][properties][Pack]`, value: this.packName },
              { name: `items[${index}][properties][_pack_id]`, value: this.packId },
              // Cart transform « Mix and match » (Smart Functions) : regroupe les lignes
              // en 1 bundle par valeur — doit être identique à _pack_id.
              { name: `items[${index}][properties][_bundleUniqueId]`, value: this.packId },
              { name: `items[${index}][properties][_pack_slot]`, value: slotValue },
            ];
            inputs.forEach(({ name, value }) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = name;
              input.value = value;
              this.formInputs.appendChild(input);
            });
            index += 1;
          }
        });
      }

      persistSelection() {
        const data = { packId: this.packId, items: [] };
        this.selection.forEach((entry, variantId) => data.items.push({ id: variantId, qty: entry.qty }));
        sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      }

      restoreSelection() {
        // Le paramètre URL (?selection=<variantId>:<qty>,...) prime — utilisé par « Modifier mon pack »
        const urlSelection = new URLSearchParams(window.location.search).get('selection');
        if (urlSelection) {
          urlSelection.split(',').forEach((pair) => {
            const [id, qty] = pair.split(':');
            const line = this.querySelector(`.pack-gifting--line[data-variant-id="${id}"]`);
            // Variante absente (mode « retirée ») ou épuisée : ignorée
            if (line && line.dataset.available === 'true' && parseInt(qty, 10) > 0) {
              // Les doublons s'agrégent (1 ligne panier par unité → ?selection=id:1,id:1)
              const existing = this.selection.get(id);
              const aggregated = (existing ? existing.qty : 0) + parseInt(qty, 10);
              this.selection.set(id, {
                qty: Math.min(aggregated, this.totalSlots),
                price: parseInt(line.dataset.price, 10),
                swatchBg: line.dataset.swatchBg,
              });
            }
          });
          if (this.selection.size > 0 && !this.packId) this.packId = `${this.packPrefix}${Date.now().toString(36)}`;
          return;
        }

        const saved = JSON.parse(sessionStorage.getItem(this.storageKey)) || null;
        if (!saved) return;
        this.packId = saved.packId;
        (saved.items || []).forEach(({ id, qty }) => {
          const line = this.querySelector(`.pack-gifting--line[data-variant-id="${id}"]`);
          if (line && line.dataset.available === 'true') {
            this.selection.set(id, {
              qty: qty,
              price: parseInt(line.dataset.price, 10),
              swatchBg: line.dataset.swatchBg,
            });
          }
        });
      }

      /* --- Lightbox galerie coloris (clic sur la pastille) --- */

      buildLightbox() {
        this.lightbox = document.createElement('div');
        this.lightbox.className = 'pack-gifting-lightbox';
        this.lightbox.setAttribute('hidden', '');
        this.lightbox.innerHTML = `
          <div class="pack-gifting-lightbox__overlay" data-lightbox-close></div>
          <div class="pack-gifting-lightbox__dialog" role="dialog" aria-modal="true">
            <button type="button" class="pack-gifting-lightbox__close" data-lightbox-close aria-label="Fermer">&times;</button>
            <button type="button" class="pack-gifting-lightbox__arrow pack-gifting-lightbox__arrow--prev" aria-label="Image précédente">&lsaquo;</button>
            <div class="pack-gifting-lightbox__track"></div>
            <button type="button" class="pack-gifting-lightbox__arrow pack-gifting-lightbox__arrow--next" aria-label="Image suivante">&rsaquo;</button>
            <div class="pack-gifting-lightbox__loader" hidden><span class="pack-gifting-lightbox__spinner"></span></div>
            <div class="pack-gifting-lightbox__footer">
              <div class="pack-gifting-lightbox__meta">
                <button type="button" class="pack-gifting-lightbox__img-nav" data-img-prev aria-label="Image précédente">&lsaquo;</button>
                <span class="pack-gifting-lightbox__color-name"></span>
                <div class="pack-gifting-lightbox__dots"></div>
                <button type="button" class="pack-gifting-lightbox__img-nav" data-img-next aria-label="Image suivante">&rsaquo;</button>
              </div>
              <div class="pack-gifting-lightbox__action"></div>
            </div>
          </div>`;

        // Configurateur injecté dans la modale pack : la galerie est intégrée à la
        // fenêtre (elle remplace le configurateur, desktop) au lieu d'empiler une
        // 2e popup. Sur la page /pages/pack-gifting, comportement inchangé.
        this.lightboxModalDialog = this.closest('.pack-gifting-modal__dialog');
        if (this.lightboxModalDialog) {
          this.lightbox.classList.add('pack-gifting-lightbox--in-modal');
          this.lightboxModalDialog.appendChild(this.lightbox);
        } else {
          document.body.appendChild(this.lightbox);
        }

        this.lightboxTrack = this.lightbox.querySelector('.pack-gifting-lightbox__track');
        this.lightboxDots = this.lightbox.querySelector('.pack-gifting-lightbox__dots');

        this.lightbox.querySelectorAll('[data-lightbox-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeLightbox());
        });
        this.lightbox
          .querySelector('.pack-gifting-lightbox__arrow--prev')
          .addEventListener('click', () => this.goToImage(this.lightboxIndex - 1));
        this.lightbox
          .querySelector('.pack-gifting-lightbox__arrow--next')
          .addEventListener('click', () => this.goToImage(this.lightboxIndex + 1));
        this.lightbox.querySelector('[data-img-prev]').addEventListener('click', () => this.goToImage(this.lightboxIndex - 1));
        this.lightbox.querySelector('[data-img-next]').addEventListener('click', () => this.goToImage(this.lightboxIndex + 1));

        // Swipe / scroll : synchronise dots et flèches
        this.lightboxTrack.addEventListener(
          'scroll',
          () => {
            if (this.lightboxScrollRaf) return;
            this.lightboxScrollRaf = requestAnimationFrame(() => {
              this.lightboxScrollRaf = null;
              const index = Math.round(this.lightboxTrack.scrollLeft / this.lightboxTrack.clientWidth);
              if (index !== this.lightboxIndex) {
                this.lightboxIndex = index;
                this.updateLightboxNav();
              }
            });
          },
          { passive: true }
        );
      }

      async openLightbox(line) {
        if (!this.lightbox) this.buildLightbox();
        this.lightboxLine = line;
        this.lightboxTrigger = line.querySelector('[data-open-lightbox]');

        // Nom du coloris + pastille active : instantané, avant le chargement des images
        const colorName = line.querySelector('.pack-gifting--line-name')?.textContent.trim() || '';
        this.lightbox.querySelector('.pack-gifting-lightbox__color-name').textContent = colorName;
        this.lightbox.querySelector('.pack-gifting-lightbox__dialog').setAttribute('aria-label', `Photos — ${colorName}`);
        this.syncLightboxAction();

        if (!this.lightbox.classList.contains('pack-gifting-lightbox--open')) {
          // Intégrée à la modale (desktop) : on cale le scroll du configurateur en
          // haut — la galerie (absolute inset:0) couvre exactement la fenêtre et le
          // scroll est verrouillé (CSS :has). Rétabli à la fermeture.
          if (this.lightboxModalDialog && window.matchMedia('(min-width: 750px)').matches) {
            this.lightboxModalScrollTop = this.lightboxModalDialog.scrollTop;
            this.lightboxModalDialog.scrollTop = 0;
          }
          this.lightbox.removeAttribute('hidden');
          void this.lightbox.offsetHeight; // reflow pour la transition
          this.lightbox.classList.add('pack-gifting-lightbox--open');
          // Pilote le masquage de la bulle chat (mobile) en CSS
          document.documentElement.classList.add('pack-gifting-lightbox-open');
          this.lightboxPreviousOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          document.addEventListener('keydown', this.lightboxKeydown, true);
          this.watchDrawerForLightbox();
        }

        const loader = this.lightbox.querySelector('.pack-gifting-lightbox__loader');
        loader.removeAttribute('hidden');
        this.lightboxTrack.innerHTML = '';
        this.lightboxDots.innerHTML = '';

        const product = await this.loadLineProduct(line);
        loader.setAttribute('hidden', '');
        if (line !== this.lightboxLine) return; // coloris changé pendant le fetch
        if (!product || !product.images || product.images.length === 0) {
          this.closeLightbox();
          return;
        }

        this.renderLightboxSlides(product);
        this.goToImage(this.findStartImage(product, line.dataset.variantId), true);
        this.lightbox.querySelector('.pack-gifting-lightbox__close').focus({ preventScroll: true });
      }

      closeLightbox() {
        if (!this.lightbox || this.lightbox.hasAttribute('hidden')) return;
        this.lightbox.classList.remove('pack-gifting-lightbox--open');
        document.documentElement.classList.remove('pack-gifting-lightbox-open');
        // Rétablit le scroll du configurateur (galerie intégrée à la modale)
        if (this.lightboxModalDialog && this.lightboxModalScrollTop) {
          this.lightboxModalDialog.scrollTop = this.lightboxModalScrollTop;
          this.lightboxModalScrollTop = 0;
        }
        document.documentElement.style.overflow = this.lightboxPreviousOverflow || '';
        document.removeEventListener('keydown', this.lightboxKeydown, true);
        if (this.lightboxDrawerObserver) {
          this.lightboxDrawerObserver.disconnect();
          this.lightboxDrawerObserver = null;
        }
        setTimeout(() => {
          if (!this.lightbox.classList.contains('pack-gifting-lightbox--open')) {
            this.lightbox.setAttribute('hidden', '');
          }
        }, 300);
        if (this.lightboxTrigger && document.contains(this.lightboxTrigger)) {
          this.lightboxTrigger.focus({ preventScroll: true });
        }
      }

      // Tiroir panier ouvert (ajout depuis le lightbox) → on referme le lightbox
      watchDrawerForLightbox() {
        const drawer =
          document.querySelector('deployer-cart-drawer') ||
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');
        if (!drawer) return;
        this.lightboxDrawerObserver = new MutationObserver(() => {
          if (drawer.classList.contains('active')) this.closeLightbox();
        });
        this.lightboxDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      // Fetch /products/<handle>.js, mis en cache par handle (28 coloris ≠ 28 requêtes)
      loadLineProduct(line) {
        const handle = line.dataset.productHandle;
        if (!handle) return Promise.resolve(null);
        if (!this.productCache[handle]) {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          this.productCache[handle] = fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' }).then(
            (response) => (response.ok ? response.json() : Promise.reject(new Error(response.status)))
          );
        }
        return this.productCache[handle].catch(() => null);
      }

      // Réglage « Photo affichée à l'ouverture » : 1 = photo de la variante (featured,
      // comportement initial) ; 2+ = N-ième photo de la galerie produit (ex : la 2e pour
      // éviter la photo studio/mannequin en preview). Plafonné au nombre de photos.
      findStartImage(product, variantId) {
        if (this.lightboxStartImage > 1) {
          return Math.min(this.lightboxStartImage - 1, product.images.length - 1);
        }
        const variant = (product.variants || []).find((v) => String(v.id) === String(variantId));
        const src = variant && variant.featured_image && variant.featured_image.src;
        if (!src) return 0;
        const clean = (url) => url.split('?')[0];
        const index = product.images.findIndex((img) => clean(img) === clean(src));
        return index >= 0 ? index : 0;
      }

      renderLightboxSlides(product) {
        this.lightboxTrack.innerHTML = '';
        this.lightboxDots.innerHTML = '';
        product.images.forEach((src, i) => {
          const slide = document.createElement('div');
          slide.className = 'pack-gifting-lightbox__slide';
          const img = document.createElement('img');
          img.src = src + (src.includes('?') ? '&' : '?') + 'width=1600';
          img.alt = `${product.title} — image ${i + 1}`;
          img.loading = i === 0 ? 'eager' : 'lazy';
          slide.appendChild(img);
          this.lightboxTrack.appendChild(slide);

          if (product.images.length > 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'pack-gifting-lightbox__dot';
            dot.setAttribute('aria-label', `Image ${i + 1}`);
            dot.addEventListener('click', () => this.goToImage(i));
            this.lightboxDots.appendChild(dot);
          }
        });
        this.lightboxIndex = 0;
        this.lightboxTrack.scrollLeft = 0;
        this.updateLightboxNav();
      }

      goToImage(index, instant = false) {
        const count = this.lightboxTrack.children.length;
        if (count === 0) return;
        this.lightboxIndex = Math.max(0, Math.min(index, count - 1));
        this.lightboxTrack.scrollTo({
          left: this.lightboxIndex * this.lightboxTrack.clientWidth,
          behavior: instant ? 'auto' : 'smooth',
        });
        this.updateLightboxNav();
      }

      updateLightboxNav() {
        const count = this.lightboxTrack.children.length;
        Array.from(this.lightboxDots.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.lightboxIndex);
        });
        this.lightbox.querySelector('.pack-gifting-lightbox__arrow--prev').disabled = this.lightboxIndex <= 0;
        this.lightbox.querySelector('.pack-gifting-lightbox__arrow--next').disabled =
          this.lightboxIndex >= count - 1;
        this.lightbox.querySelector('[data-img-prev]').disabled = this.lightboxIndex <= 0;
        this.lightbox.querySelector('[data-img-next]').disabled = this.lightboxIndex >= count - 1;
      }

      // CTA du pied : miroir de l'état de la ligne (Ajouter / stepper / épuisé)
      syncLightboxAction() {
        if (!this.lightbox || !this.lightboxLine) return;
        const action = this.lightbox.querySelector('.pack-gifting-lightbox__action');
        action.innerHTML = '';
        const line = this.lightboxLine;

        if (line.dataset.available !== 'true') {
          const oos = document.createElement('p');
          oos.className = 'pack-gifting-lightbox__oos';
          oos.textContent = line.querySelector('.pack-gifting--line-badge--oos')?.textContent.trim() || 'Épuisé';
          action.appendChild(oos);
          return;
        }

        const entry = this.selection.get(line.dataset.variantId);
        const qty = entry ? entry.qty : 0;
        const isComplete = this.unitCount >= this.totalSlots;

        if (qty === 0) {
          const add = document.createElement('button');
          add.type = 'button';
          add.className = 'pack-gifting--add button button--primary';
          if (isComplete) add.classList.add('pack-gifting--add--inert');
          add.textContent = line.querySelector('.pack-gifting--add')?.textContent.trim() || 'Ajouter +';
          add.addEventListener('click', () => this.addUnit(line));
          action.appendChild(add);
        } else {
          const stepper = document.createElement('div');
          stepper.className = 'pack-gifting--stepper';

          const minus = document.createElement('button');
          minus.type = 'button';
          minus.className = 'pack-gifting--stepper-btn';
          minus.textContent = '−';
          minus.setAttribute('aria-label', 'Retirer une unité');
          minus.addEventListener('click', () => this.removeUnit(line));

          const qtyEl = document.createElement('span');
          qtyEl.className = 'pack-gifting--stepper-qty';
          qtyEl.textContent = qty;

          const plus = document.createElement('button');
          plus.type = 'button';
          plus.className = 'pack-gifting--stepper-btn';
          plus.textContent = '+';
          plus.setAttribute('aria-label', 'Ajouter une unité');
          if (isComplete) plus.classList.add('pack-gifting--stepper-btn--inert');
          plus.addEventListener('click', () => this.addUnit(line));

          stepper.append(minus, qtyEl, plus);
          action.appendChild(stepper);
        }
      }
    }
  );
}
